import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { BDK, waUrl } from "../../config/bdk";

const SOCIAL_LINKS = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/bdksistemas",
    Icon: Facebook,
    hover: "#1877F2",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/bdksistemas",
    Icon: Instagram,
    hover: "#E1306C",
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/bdksistemas",
    Icon: Youtube,
    hover: "#FF0000",
  },
];

export const FinalCTA = () => {
  useReveal();
  return (
    <section
      id="cta-final"
      data-testid="section-cta-final"
      className="py-24 md:py-28 text-white text-center"
      style={{ background: "var(--red)" }}
    >
      <div
        className="container-bdk max-w-[900px] bdk-reveal bdk-reveal--up"
        style={{ margin: "0 auto" }}
      >
        <img
          src={BDK.logos.white}
          alt="BDKSISTEMAS"
          className="w-52 md:w-64 h-auto mx-auto mb-8 object-contain"
        />
        <h2 className="font-display text-[42px] sm:text-[56px] md:text-[68px] lg:text-[80px] leading-[0.95]">
          Dinos qué necesitas y te
          <br />
          ayudamos a elegir tu página.
        </h2>
        <p className="mt-6 max-w-[680px] mx-auto text-white/90 text-[15px] md:text-[16px]">
          Solicita tu propuesta y descubre cómo podría lucir tu página antes
          de tomar una decisión.
        </p>

        <div className="mt-10 flex flex-col sm:flex-row justify-center flex-wrap gap-3">
          <a
            href="#contacto"
            data-testid="final-cta-form"
            className="bdk-btn bdk-btn--white bdk-btn--lg"
          >
            Cotizar mi página web
          </a>
          <a
            href={waUrl()}
            target="_blank"
            rel="noreferrer"
            data-testid="final-cta-whatsapp"
            onClick={() => window.fbq?.("trackCustom", "ClickWhatsAppFinal")}
            className="bdk-btn bdk-btn--dark bdk-btn--lg"
          >
            Hablar por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      style={{ background: "var(--ink)", color: "#aaa" }}
    >
      <div className="container-bdk py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <img
            src={BDK.logos.redWhite}
            alt="BDKSISTEMAS"
            className="w-[200px] h-auto object-contain mb-5"
          />
          <p className="font-display text-[22px] text-white leading-tight uppercase">
            {BDK.companyLegalName}
          </p>
          <p className="text-[12px] text-white/60 mt-1">
            {BDK.companyTagline}
          </p>
          <div className="mt-6 space-y-2 text-[13px]">
            <p>
              <a
                href={`mailto:${BDK.publicEmail}`}
                className="hover:text-white"
                data-testid="footer-email"
              >
                {BDK.publicEmail}
              </a>
            </p>
            <p>
              <a
                href={waUrl()}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white"
                data-testid="footer-whatsapp"
                style={{ color: "#25d366" }}
              >
                WhatsApp: {BDK.whatsappDisplay}
              </a>
            </p>
            <p>
              <a
                href={`https://${BDK.publicWeb}`}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white"
                data-testid="footer-website"
              >
                {BDK.publicWeb}
              </a>
            </p>
          </div>

          {/* Social */}
          <div
            className="mt-6 flex items-center gap-3"
            data-testid="footer-social"
          >
            {SOCIAL_LINKS.map(({ name, href, Icon, hover }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={name}
                data-testid={`footer-social-${name.toLowerCase()}`}
                className="grid place-items-center w-10 h-10 rounded-full transition-all"
                style={{
                  border: "1px solid rgba(255,255,255,.15)",
                  color: "#aaa",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = hover;
                  e.currentTarget.style.color = hover;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,.15)";
                  e.currentTarget.style.color = "#aaa";
                  e.currentTarget.style.transform = "";
                }}
              >
                <Icon size={17} />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-[16px] text-white uppercase mb-4">
            Navegación
          </h4>
          <ul className="space-y-2 text-[13px]">
            {[
              { l: "Páginas Web", h: "/#tipos-paginas" },
              { l: "Proyectos", h: "/#portfolio" },
              { l: "Cómo funciona", h: "/#proceso" },
              { l: "Preguntas frecuentes", h: "/#faq" },
              { l: "Contacto", h: "/#contacto" },
            ].map((x) => (
              <li key={x.h}>
                <a href={x.h} className="hover:text-white">
                  {x.l}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-display text-[16px] text-white uppercase mb-4">
            Servicios
          </h4>
          <ul className="space-y-2 text-[13px]">
            <li>Landing page</li>
            <li>Sitio web empresarial</li>
            <li>Catálogo / citas / tienda en línea</li>
            <li>Desarrollo a la medida</li>
            <li>Mantenimiento web</li>
            <li>
              <Link
                to="/otros-servicios"
                data-testid="footer-otros-servicios"
                onClick={() => window.fbq?.("trackCustom", "ClickOtrosServicios")}
                className="hover:text-white"
              >
                Otros servicios creativos →
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-[16px] text-white uppercase mb-4">
            Legal
          </h4>
          <ul className="space-y-2 text-[13px]">
            <li>
              <a
                href="/aviso-de-privacidad.html"
                data-testid="footer-privacy-link"
                className="hover:text-white"
              >
                Aviso de privacidad
              </a>
            </li>
            <li>
              <a
                href="/terminos-y-condiciones.html"
                data-testid="footer-terms-link"
                className="hover:text-white"
              >
                Términos y condiciones
              </a>
            </li>
          </ul>
          <p className="mt-6 text-[10px] text-white/40 leading-relaxed">
            Precios en pesos mexicanos, sujetos a cambio sin previo aviso. No se
            garantizan ventas específicas.
          </p>
        </div>
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="container-bdk py-5 text-[11px] text-white/40 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>
            © {new Date().getFullYear()} {BDK.companyLegalName}. Todos los
            derechos reservados.
          </span>
          <span className="font-display uppercase tracking-widest text-white/60">
            Diseñado por BDKSISTEMAS
          </span>
        </div>
      </div>
    </footer>
  );
};
